import React, { useState, useEffect } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { getMultiSubBrandDetails } from '../../../SystemMasters/Store/SystemMasters.action';

// Ensure you install a switch component library or use your custom switch

export function BasicLibraryDetail({
  libraryData,
  errorFields,
  handleBasicDetail,
  brandsOptions,
  productTypeOptions,
  supplierOptions,
  lengthEnabled,
  canvasData,
  isSubBrandAccess,
  subBrandOptions,
}) {
  const { t } = useTranslation('common');
  const [productJourney, setProductJourney] = useState([]);
  const [productTypeError, setProductTypeError] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    const journeyProductTypes =
      canvasData
        ?.filter((data) => data?.key === 'product_journey')
        ?.flatMap((data) => data?.productType || [])
        ?.filter(Boolean) || [];

    setProductJourney(journeyProductTypes);
  }, [canvasData]);

  const handleProductTypeChange = (selectedOptions) => {
    const currentProductTypes = libraryData?.productTypeId || [];
    const removedProductTypes = currentProductTypes.filter((type) => !selectedOptions.some((opt) => opt.value === type.value));
    const usedInJourney = removedProductTypes.some((type) => productJourney.includes(type.value));

    if (usedInJourney) {
      setProductTypeError('This product type is being used in the product journey. Please remove it from the journey first.');
      return;
    }

    setProductTypeError('');
    handleBasicDetail('productTypeId', selectedOptions);
  };

  useEffect(() => {
    if (!isEmpty(libraryData?.brandId)) {
      const queryParams = {
        brandId: libraryData?.brandId?.map((item) => item.value),
      };
      dispatch(getMultiSubBrandDetails(queryParams));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryData?.brandId]);

  useEffect(() => {
    if (subBrandOptions?.length > 0 && libraryData?.subBrandId?.length > 0 && isSubBrandAccess) {
      const availableSubBrandIds = subBrandOptions.map((option) => option._id);
      const filteredSelectedSubBrands = libraryData.subBrandId.filter((subBrand) => availableSubBrandIds.includes(subBrand._id || subBrand.value));

      if (filteredSelectedSubBrands?.length !== libraryData?.subBrandId?.length) {
        handleBasicDetail('subBrandId', filteredSelectedSubBrands);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subBrandOptions, libraryData?.brandId]);

  return (
    <Card className="card custom-card card-body basic-detail mb-2">
      <Row>
        <Col md={10}>
          <Row>
            <Col md={3}>
              <div className="common-input-control mb-0">
                <p className="field-name">
                  {t('DPP.BASIC_DETAILS.NAME')}
                  <span className="asterisk">*</span>
                </p>
                <input
                  type="text"
                  name="dppName"
                  value={libraryData?.dppName}
                  onChange={(e) => handleBasicDetail('dppName', e.target.value)}
                  placeholder={t('DPP.BASIC_DETAILS.NAME')}
                  className="form-control"
                />
                {errorFields?.includes('dppName') && libraryData?.dppName?.trim()?.length === 0 && (
                  <p className="mb-0 error-message">{t('DPP.BASIC_DETAILS.NAME_REQUIRED')}</p>
                )}
                {errorFields?.includes('dppName') && libraryData?.dppName?.trim()?.length !== 0 && (
                  <p className="mb-0 error-message">{t('DPP.BASIC_DETAILS.NAME_SPECIAL_CHARACTER_NOT_ALLOWED')}</p>
                )}
              </div>
            </Col>
            <Col md={3}>
              <div className="common-react-select mb-0">
                <p className="field-name">
                  {t('DPP.BASIC_DETAILS.BRAND')}
                  <span className="asterisk">*</span>
                </p>
                <MultiSelect
                  options={brandsOptions}
                  className="product-custom-multiselect common-new-multiselect w-100"
                  value={libraryData?.brandId || []}
                  labelledBy={t('DPP.BASIC_DETAILS.SELECT_BRAND')}
                  overrideStrings={{
                    selectSomeItems: t('DPP.BASIC_DETAILS.SELECT_BRAND'),
                    search: t('DPP.BASIC_DETAILS.SEARCH_BRAND'),
                  }}
                  ClearSelectedIcon={null}
                  onChange={(selected) => handleBasicDetail('brandId', selected)}
                />
                {errorFields?.includes('brandId') && <p className="mb-0 error-message">{t('DPP.BASIC_DETAILS.BRAND_REQUIRED')}</p>}
              </div>
            </Col>
            {isSubBrandAccess && (
              <Col md={3}>
                <div className="common-react-select mb-0">
                  <p className="field-name">{t('POMS.GRADING_GROUP.TOP_GROUP_BAR.SUB_BRAND')}</p>
                  <MultiSelect
                    name="SubBrand"
                    options={subBrandOptions?.filter((e) => e.isActive) || []}
                    className="product-custom-multiselect common-new-multiselect"
                    value={libraryData?.subBrandId || []}
                    overrideStrings={{
                      selectSomeItems: t('DPP.BASIC_DETAILS.SELECT_SUB_BRAND'),
                      search: t('DPP.BASIC_DETAILS.SELECT_SUB_BRAND'),
                    }}
                    onChange={(selected) => handleBasicDetail('subBrandId', selected)}
                  />
                </div>
              </Col>
            )}
            <Col md={3}>
              <div className="common-react-select mb-0">
                <p className="field-name">
                  {t('DPP.BASIC_DETAILS.PRODUCT_TYPE')}
                  <span className="asterisk">*</span>
                </p>
                <MultiSelect
                  options={productTypeOptions}
                  className="product-custom-multiselect common-new-multiselect w-100"
                  value={libraryData?.productTypeId || []}
                  labelledBy={t('DPP.BASIC_DETAILS.SELECT_PRODUCT_TYPE')}
                  overrideStrings={{
                    selectSomeItems: t('DPP.BASIC_DETAILS.SELECT_PRODUCT_TYPE'),
                    search: t('DPP.BASIC_DETAILS.SEARCH_PRODUCT_TYPE'),
                  }}
                  ClearSelectedIcon={null}
                  onChange={handleProductTypeChange}
                />
                {productTypeError && <p className="mb-0 error-message">{productTypeError}</p>}
                {errorFields?.includes('productTypeId') && !productTypeError && (
                  <p className="mb-0 error-message">{t('DPP.BASIC_DETAILS.PRODUCT_TYPE_REQUIRED')}</p>
                )}
              </div>
            </Col>
            <Col md={3}>
              <div className="common-react-select mb-0">
                <p className="field-name mt-3">
                  {t('DPP.BASIC_DETAILS.SUPPLIER')}
                  <span className="asterisk">*</span>
                </p>
                <MultiSelect
                  options={supplierOptions}
                  className="product-custom-multiselect common-new-multiselect w-100"
                  value={libraryData?.supplierId || []}
                  labelledBy={t('DPP.BASIC_DETAILS.SELECT_SUPPLIER')}
                  overrideStrings={{
                    selectSomeItems: t('DPP.BASIC_DETAILS.SELECT_SUPPLIER'),
                    search: t('DPP.BASIC_DETAILS.SEARCH_SUPPLIER'),
                  }}
                  ClearSelectedIcon={null}
                  onChange={(selected) => handleBasicDetail('supplierId', selected)}
                />
                {errorFields?.includes('supplierId') && <p className="mb-0 error-message">{t('DPP.BASIC_DETAILS.SUPPLIER_REQUIRED')}</p>}
              </div>
            </Col>
          </Row>
        </Col>
        <Col md={2}>
          <div className="d-flex flex-column pl-16">
            <div className="d-flex align-items-center">
              <div className="switch-common d-flex design-switch">
                <input
                  id="customSwitch1"
                  type="checkbox"
                  className="form-check-input"
                  checked={libraryData?.colorwayToggel || false}
                  name="colorwayToggel"
                  onChange={(e) => handleBasicDetail('colorwayToggel', e.target.checked)}
                />
                <label className="text-nowrap design-title text-black" htmlFor="customSwitch1">
                  Colourway
                </label>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <div className="switch-common d-flex design-switch">
                <input
                  id="customSwitch2"
                  type="checkbox"
                  className="form-check-input "
                  checked={libraryData?.sizeLengthToggel || false}
                  name="sizeLengthToggel"
                  onChange={(e) => handleBasicDetail('sizeLengthToggel', e.target.checked)}
                />
                <label className="text-nowrap design-title text-black" htmlFor="customSwitch2">
                  {lengthEnabled ? 'Size and Length' : 'Size'}
                </label>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
}

export default BasicLibraryDetail;
